import { useState, useEffect, useRef } from 'react';
import { useOpenAiCompletion, COMPLETION_STATUS } from '@client/hooks/useOpenAiCompletion';
import { useConcurrentSearch, clearUnderwritingCacheForPromptType } from '@client/hooks/useConcurrentSearch';
import { Button } from '@/components/ui/button';
import { SheetModal } from '@/components/SheetModal';
import { Loader2, Sparkles, CircleX, RefreshCw, AlertCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  postAiTypeformPayloadByClientIdRequest,
  resetAiUnderwritingTypeformState,
} from '@client/model/aiUnderwritingTypeformModel';
import { selectAiUnderwritingTypeform, selectMaybeClientPartner } from '@client/model/selectors';
import {
  FundingPolicyType,
  getAiPromptTypeByPolicyType,
  isOutdatedVersion,
  logInfo,
  parseUnderwritingGuidanceResponse,
  policyStepConfigs,
  UNDERWRITING_PROMPT_VERSIONS,
} from '@shared';
import { AiUnderwritingGuidanceResultComponent } from '@client/components/AiUnderwritingGuidanceResultComponent';

interface AiUnderwritingGuidanceProps {
  clientId: string;
  formData: PolicyFormSubmissionData;
  buttonText?: string;
  buttonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  disableAbsolutePosition?: boolean;
  forceRefresh?: boolean;
}

export function AiUnderwritingGuidance({
  clientId,
  formData,
  buttonText = 'Initial Insights',
  buttonVariant = 'secondary',
  buttonSize = 'default',
  className = '',
  disableAbsolutePosition = false,
  forceRefresh = false,
}: AiUnderwritingGuidanceProps) {
  const dispatch = useDispatch();
  const aiUnderwritingTypeform = useSelector(selectAiUnderwritingTypeform);
  const typeformPayload = aiUnderwritingTypeform.data.client;
  const isTypeformLoading = aiUnderwritingTypeform.isLoading.client;
  const typeformError = aiUnderwritingTypeform.error.client;
  const partnerTypeformPayload = aiUnderwritingTypeform.data.partner;
  const isPartnerTypeformLoading = aiUnderwritingTypeform.isLoading.partner;
  const clientPartner = useSelector(selectMaybeClientPartner);

  const { createCompletion, status, result, error, errorStatusCode } = useOpenAiCompletion({
    pollingInterval: 4000,
    maxPollingAttempts: 80,
  });
  const concurrentSearch = useConcurrentSearch('ai_underwriting_guidance');
  const [isOpen, setIsOpen] = useState(false);
  const [hasFetchedTypeform, setHasFetchedTypeform] = useState(false);
  const [searchResult, setSearchResult] = useState<OpenAiCompletionDbRecord | null>(null);
  const [shouldForceRefresh, setShouldForceRefresh] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retriedAiPromptType, setRetriedAiPromptType] = useState<string | null>(null);
  const previousFormDataRef = useRef<PolicyFormSubmissionData | null>(null);

  // Detect formData changes and set force refresh flag
  useEffect(() => {
    const previousFormData = previousFormDataRef.current;
    const currentFormData = formData;

    // On first render, store the initial formData
    if (previousFormData === null) {
      previousFormDataRef.current = currentFormData;
      setShouldForceRefresh(false);
      return;
    }

    // Compare form data to detect meaningful changes (ignoring timestamps, etc.)
    const formDataChanged = JSON.stringify(previousFormData) !== JSON.stringify(currentFormData);

    if (formDataChanged) {
      logInfo('AiUnderwritingGuidance', 'Form data changed, will force refresh on next analysis', {
        previousFormData: previousFormData,
        currentFormData: currentFormData,
      });
      setShouldForceRefresh(true);
      previousFormDataRef.current = currentFormData;
    }
  }, [formData]);

  const handleGetUnderwritingGuidance = (forceRefreshOverride = false) => {
    if (!clientId) {
      logInfo('AiUnderwritingGuidance', 'No client data available for underwriting analysis', {});
      return;
    }

    // Set retry state when forcing refresh
    if (forceRefreshOverride) {
      setIsRetrying(true);
    }

    // Only clear search results if not retrying (to preserve results on rate limit error)
    if (!forceRefreshOverride) {
      setSearchResult(null);
    }

    logInfo('AiUnderwritingGuidance', 'Starting AI underwriting guidance', {
      policyType: formData?.policyType,
      forceRefreshOverride,
    });

    // Check if formData is empty or minimal, and we have typeform data
    const isFormDataEmpty = !formData || Object.keys(formData).length <= 3;
    let dataToUse =
      isFormDataEmpty && typeformPayload?.typeformPayload
        ? { ...formData, clientData: typeformPayload.typeformPayload }
        : formData;

    // For joint policies, include partner intake data if available
    const isJointPolicy =
      formData?.policyType && policyStepConfigs[formData.policyType as FundingPolicyType]?.isJoint;
    if (isJointPolicy) {
      if (partnerTypeformPayload?.typeformPayload) {
        logInfo('AiUnderwritingGuidance', 'Including partner intake data in AI analysis', {
          hasPartnerData: true,
          partnerClientId: clientPartner?.clientId,
        });
        dataToUse = {
          ...dataToUse,
          partnerData: partnerTypeformPayload.typeformPayload,
        };
      } else if (clientPartner?.clientId) {
        logInfo('AiUnderwritingGuidance', 'Partner exists but no typeform data received', {
          partnerClientId: clientPartner.clientId,
        });
      }
    }

    const aiPromptType = getAiPromptTypeByPolicyType(formData);

    // Use forceRefresh prop, shouldForceRefresh state, or forceRefreshOverride parameter to determine if we should skip search
    const shouldSkipSearch = forceRefresh || shouldForceRefresh || forceRefreshOverride;

    if (shouldSkipSearch) {
      // Reset the force refresh flag since we're acting on it
      if (shouldForceRefresh) {
        setShouldForceRefresh(false);
      }
      // Pass isRetry=true when user explicitly clicks "Rerun Analysis" (forceRefreshOverride=true)
      createNewCompletion(aiPromptType, dataToUse, forceRefreshOverride);
    } else {
      // Search for existing completion first
      concurrentSearch.startSearch(
        {
          requesterId: clientId,
          aiPromptType: aiPromptType,
        },
        {
          onSuccess: data => {
            setSearchResult(data);
          },
          onError: () => {
            // No existing completion found, create new one
            createNewCompletion(aiPromptType, dataToUse);
          },
        },
      );
    }
  };

  const createNewCompletion = (aiPromptType: string, dataToUse: any, isRetry = false) => {
    const promptVersion = UNDERWRITING_PROMPT_VERSIONS[aiPromptType];

    // Store the aiPromptType when retrying so we can use it later to clear the cache
    if (isRetry) {
      setRetriedAiPromptType(aiPromptType);
    }

    createCompletion('', {
      model: 'o3',
      clientId: clientId,
      aiPromptType: aiPromptType,
      metadata: { ...dataToUse },
      isRetry: isRetry,
      promptVersion,
    });
  };

  useEffect(() => {
    if (isOpen && clientId && !hasFetchedTypeform) {
      // Fetch typeform data when modal opens for the first time
      dispatch(postAiTypeformPayloadByClientIdRequest({ clientId, target: 'client' }));
      setHasFetchedTypeform(true);

      // For joint policies, also fetch partner's intake form if partner exists
      const isJointPolicy =
        formData?.policyType &&
        policyStepConfigs[formData.policyType as FundingPolicyType]?.isJoint;
      if (isJointPolicy && clientPartner?.clientId) {
        logInfo('AiUnderwritingGuidance', 'Fetching partner typeform data', {
          partnerClientId: clientPartner.clientId,
          policyType: formData.policyType,
        });
        dispatch(
          postAiTypeformPayloadByClientIdRequest({
            clientId: clientPartner.clientId,
            target: 'partner',
          }),
        );
      }
    }
  }, [isOpen, clientId, hasFetchedTypeform, dispatch, formData?.policyType, clientPartner]);

  // Trigger AI underwriting when all data is ready
  useEffect(() => {
    if (!isOpen || !clientId || isTypeformLoading || isPartnerTypeformLoading) return;

    const isJointPolicy =
      formData?.policyType && policyStepConfigs[formData.policyType as FundingPolicyType]?.isJoint;
    const needsPartnerData = isJointPolicy && clientPartner?.clientId;

    // Check if we have the required data: client data always, partner data only if needed
    const hasRequiredData = typeformPayload && (!needsPartnerData || partnerTypeformPayload);
    const canProceedWithError = !needsPartnerData && typeformError;

    if (hasRequiredData || canProceedWithError) {
      handleGetUnderwritingGuidance();
    }
  }, [
    isOpen,
    clientId,
    isTypeformLoading,
    isPartnerTypeformLoading,
    typeformPayload,
    partnerTypeformPayload,
    typeformError,
    formData?.policyType,
    clientPartner,
  ]);

  // Clean up typeform state when modal closes
  useEffect(() => {
    if (!isOpen && hasFetchedTypeform) {
      dispatch(resetAiUnderwritingTypeformState());
      setHasFetchedTypeform(false);
    }
  }, [isOpen, hasFetchedTypeform, dispatch]);

  // Handle completion status changes
  useEffect(() => {
    if (status === COMPLETION_STATUS.ERROR) {
      // Clear retry state on error
      setIsRetrying(false);
      setRetriedAiPromptType(null);
    } else if (status === COMPLETION_STATUS.COMPLETED && result) {
      // Update search result with new result when polling completes
      if (isRetrying && result.results) {
        // Use the stored aiPromptType from when we started the retry
        const aiPromptType = retriedAiPromptType || result.aiPromptType;
        logInfo('AiUnderwritingGuidance', 'Retry completed successfully, updating results and invalidating cache', {
          aiPromptType,
          retriedAiPromptType,
          resultAiPromptType: result.aiPromptType,
        });
        setSearchResult(result);

        // Invalidate the cache so the new result will be fetched everywhere
        // (FundingTable, ShopInsuranceModal, etc.)
        if (aiPromptType) {
          clearUnderwritingCacheForPromptType(dispatch, aiPromptType); // Clear carrier-specific caches
        }

        // Clear the stored retry aiPromptType
        setRetriedAiPromptType(null);
      }

      // Clear retry state on completion
      setIsRetrying(false);
    }
  }, [status, result, isRetrying, retriedAiPromptType, dispatch]);

  // Use search result if available, otherwise use completion result
  const currentResult = searchResult || result;
  const parsedResults = currentResult?.results
    ? parseUnderwritingGuidanceResponse(currentResult.results)
    : null;

  // Detect if the error is a rate limit error
  const isRateLimitError =
    (typeof errorStatusCode === 'string' && errorStatusCode === '429') ||
    (typeof errorStatusCode === 'number' && errorStatusCode === 429);

  const aiPromptType = getAiPromptTypeByPolicyType(formData);

  // Check if the search result (not new result) is from an older generation
  // Only show banner for search results that are outdated, not for newly created results
  const isOutdatedPrompt = isOutdatedVersion(searchResult?.promptVersion, aiPromptType);

  return (
    <div
      className={`mb-2 flex ${disableAbsolutePosition ? '' : 'lg:absolute lg:right-0'} justify-start md:justify-center`}
    >
      <Button
        onClick={() => setIsOpen(true)}
        variant={buttonVariant}
        size={buttonSize}
        className={`flex items-center gap-2 ${className}`}
        data-testid="get-underwriting-guidance"
      >
        <Sparkles className="h-4 w-4" />
        {buttonText}
      </Button>

      <SheetModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Initial AI Underwriting Preview"
        subTitle="AI-powered analysis of underwriting factors"
        modal={true}
        className="sm:min-w-[55%] md:min-w-[550px] lg:min-w-[500px]"
      >
        <div className="flex min-h-full flex-1 flex-col overflow-auto">
          <div
            className={`flex flex-1 flex-col py-4 ${currentResult?.results ? '' : 'justify-center'}`}
          >
            {/* Loading States - Show when initially loading OR when retrying with existing results */}
            {(isTypeformLoading ||
              isPartnerTypeformLoading ||
              concurrentSearch.isSearching ||
              status === COMPLETION_STATUS.CREATING ||
              status === COMPLETION_STATUS.POLLING) &&
              (!parsedResults || isRetrying) && (
                <div className="space-y-4 text-center" data-testid="underwriting-loading">
                  <Loader2 className="text-primary-dark mx-auto h-8 w-8 animate-spin" />
                  <p className="font-medium text-gray-700">
                    {isRetrying
                      ? 'Rerunning analysis...'
                      : concurrentSearch.isSearching
                        ? 'Searching for existing insights...'
                        : 'Getting insights...'}
                  </p>
                  <p className="text-sm text-gray-500">This may take a few moments</p>
                </div>
              )}

            {/* Rate Limit Error Banner - Show with existing results */}
            {isRateLimitError && error && parsedResults && (
              <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-500" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-orange-800">Request Limit Reached</h3>
                    <p className="mt-1 text-sm text-orange-700">{error}</p>
                    <p className="mt-2 text-sm text-orange-600">
                      You can view your previous results below. Please try again later.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error State - Only show if no results available */}
            {status === COMPLETION_STATUS.ERROR && error && !isRateLimitError && !parsedResults && (
              <div className="space-y-4 text-center" data-testid="underwriting-error">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-center justify-center space-x-2">
                    <CircleX className="h-5 w-5 text-red-400" />
                    <h3 className="text-sm font-medium text-red-800">Analysis Error</h3>
                  </div>
                  <div className="mt-2 text-sm text-red-700" data-testid="error-message">
                    <p>{error}</p>
                  </div>
                  <div className="mt-4">
                    <Button
                      onClick={() => handleGetUnderwritingGuidance()}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      data-testid="retry-button"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Results Display - Hide when retrying */}
            {parsedResults && !isRetrying && (
              <>
                {/* Retry Banner - Only show if generation is outdated and not a rate limit error */}
                {isOutdatedPrompt && !isRateLimitError && (
                  <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm text-blue-800">
                          We've made recent updates to our AI models. If your results don't seem fully
                          accurate, we recommend rerunning the analysis.
                        </p>
                        <Button
                          onClick={() => {
                            logInfo('AiUnderwritingGuidance', 'Retrying AI underwriting with fresh data', {});
                            handleGetUnderwritingGuidance(true);
                          }}
                          variant="link"
                          size="sm"
                          className="mt-2 h-auto p-0 text-blue-700 hover:text-blue-900"
                        >
                          <RefreshCw className="mr-1 h-3 w-3" />
                          Rerun Analysis
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <AiUnderwritingGuidanceResultComponent parsedResults={parsedResults} />

                {/* Refresh button at the bottom */}
                <div className="mt-6 flex justify-center border-t pt-4">
                  <Button
                    onClick={() => {
                      logInfo('AiUnderwritingGuidance', 'Rerunning analysis from bottom button', {});
                      handleGetUnderwritingGuidance(true);
                    }}
                    variant="outline"
                    size="sm"
                    disabled={status === COMPLETION_STATUS.CREATING || status === COMPLETION_STATUS.POLLING}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Rerun Analysis
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </SheetModal>
    </div>
  );
}
